// Pure geometrie voor het slepen/resizen/roteren van canvas-frames in de
// editor. Mirrort de stijl van canvas/cameraMath.ts: geen React, geen DOM,
// puur en unit-testbaar.

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const MIN_SIZE = 20;

// Elke handle-naam hoort bij het TEGENOVERGESTELDE ankerpunt (als fractie
// van breedte/hoogte, 0=links/boven, 1=rechts/onder) -- dat ankerpunt blijft
// bij het resizen vast op zijn bordpositie te staan.
const HANDLE_ANCHOR_FRACTION: Record<ResizeHandle, { x: number; y: number }> = {
  n: { x: 0.5, y: 1 },
  s: { x: 0.5, y: 0 },
  e: { x: 0, y: 0.5 },
  w: { x: 1, y: 0.5 },
  ne: { x: 0, y: 1 },
  nw: { x: 1, y: 1 },
  se: { x: 0, y: 0 },
  sw: { x: 1, y: 0 },
};

/**
 * Zet een schermpixel-verplaatsing om naar een verplaatsing in het frame's
 * EIGEN, ongedraaide lokale ruimte -- eerst terugschalen naar bordpixels
 * (delen door de statische contain-fit-zoomschaal van de editor-stage, geen
 * geanimeerde camera zoals cameraMath.ts), dan de inverse rotatie toepassen.
 * Bij rotation=0 degenereert dit vanzelf tot gewoon boardDx/boardDy.
 */
export function screenDeltaToLocalDelta(
  screenDx: number, screenDy: number, rotationDeg: number, zoomScale: number,
): { dx: number; dy: number } {
  const boardDx = screenDx / zoomScale;
  const boardDy = screenDy / zoomScale;
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    dx: boardDx * cos + boardDy * sin,
    dy: -boardDx * sin + boardDy * cos,
  };
}

/**
 * Resize een frame door één handle te slepen, met het TEGENOVERGESTELDE
 * ankerpunt vast op zijn bordpositie (het universele resize-gevoel uit elk
 * grafisch programma). localDx/localDy komen uit screenDeltaToLocalDelta
 * hierboven en zijn dus al in het frame's eigen lokale ruimte -- dezelfde
 * formule werkt daardoor identiek voor rotation=0 (gewoon axis-aligned) en
 * een niet-triviale rotatie, geen aparte tak nodig. Rand-handles (n/s/e/w)
 * nullen expliciet de loodrechte delta zodat ze niet per ongeluk ook de
 * andere as resizen. Breedte/hoogte klemmen op een minimum i.p.v. negatief
 * te worden -- geen flip/handle-remap in v1, bewust simpel gehouden.
 */
export function resizeFrame(frame: Box & { rotation?: number }, handle: ResizeHandle, localDx: number, localDy: number): Box {
  const affectsWidth = handle.includes('e') || handle.includes('w');
  const affectsHeight = handle.includes('n') || handle.includes('s');
  const dx = affectsWidth ? localDx : 0;
  const dy = affectsHeight ? localDy : 0;
  const widthSign = handle.includes('e') ? 1 : handle.includes('w') ? -1 : 0;
  const heightSign = handle.includes('s') ? 1 : handle.includes('n') ? -1 : 0;

  const newWidth = Math.max(MIN_SIZE, frame.width + dx * widthSign);
  const newHeight = Math.max(MIN_SIZE, frame.height + dy * heightSign);

  const rad = ((frame.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const oldCenter = { x: frame.x + frame.width / 2, y: frame.y + frame.height / 2 };
  const anchor = HANDLE_ANCHOR_FRACTION[handle];

  const oldAnchorLocalOffset = { x: (anchor.x - 0.5) * frame.width, y: (anchor.y - 0.5) * frame.height };
  const anchorBoard = {
    x: oldCenter.x + oldAnchorLocalOffset.x * cos - oldAnchorLocalOffset.y * sin,
    y: oldCenter.y + oldAnchorLocalOffset.x * sin + oldAnchorLocalOffset.y * cos,
  };

  const newAnchorLocalOffset = { x: (anchor.x - 0.5) * newWidth, y: (anchor.y - 0.5) * newHeight };
  const newCenter = {
    x: anchorBoard.x - (newAnchorLocalOffset.x * cos - newAnchorLocalOffset.y * sin),
    y: anchorBoard.y - (newAnchorLocalOffset.x * sin + newAnchorLocalOffset.y * cos),
  };

  return {
    x: newCenter.x - newWidth / 2,
    y: newCenter.y - newHeight / 2,
    width: newWidth,
    height: newHeight,
  };
}

/** Nieuwe rotatie (in graden) op basis van het verschil tussen de huidige en
 * de start-hoek van de aanwijzer t.o.v. het frame-midden (beide in radialen,
 * bv. via Math.atan2(pointerY - centerY, pointerX - centerX)). */
export function rotateFromPointer(pointerAngleRad: number, startAngleRad: number, startRotationDeg: number): number {
  const deltaDeg = ((pointerAngleRad - startAngleRad) * 180) / Math.PI;
  return startRotationDeg + deltaDeg;
}
